import { Guid } from "@/util/guid";
import _ from "lodash";
import eventLogger from "@services/ApiLogger/EventLogger.js";
import automationApi from "@services/automationServer";
import { generateNewNameMultiLanguage as generateNewName } from "@util/generateNewName";
import i18n from "@/i18n";

const { t } = i18n.global;

const defaultState = () => ({
  automations: [],
  automationTemplates: [],
  actionTypes: [],
  triggerTypes: [],
  selectedAutomation: {},
  metadata: {
    loading: false,
    isSaving: false,
    hasUnsavedData: false,
    lastRunSuccess: true,
    updateSeen: true,
    preventSaving: false
  }
});

export default {
  namespaced: true,
  state: { ...defaultState() },
  getters: {
    hasUnsavedData(state) {
      return state.metadata.hasUnsavedData;
    }
  },
  mutations: {
    "reset-state"(state) {
      Object.assign(state, defaultState());
    },
    setCurrentAutomation(state, arg) {
      state.selectedAutomation = arg;
    },
    setAllAutomations(state, arg) {
      state.automations = arg;
    },
    setAutomationTemplates(state, arg) {
      state.automationTemplates = arg;
    },
    addToAutomations(state, arg) {
      const existingAutomation = state.automations.find((x) => x.automationId == arg?.automationId);
      if (existingAutomation) {
        Object.assign(existingAutomation, arg);
      } else {
        state.automations.push(arg);
      }
    },
    updateStatus(state, args) {
      state.automations.find((x) => x.automationId == args.automationId).isActive = args.isActive;
    },
    deleteAutomations(state, automationIds) {
      automationIds.forEach((x) => {
        const index = state.automations.findIndex((item) => item.automationId == x);
        state.automations.splice(index, 1);
      });
    },
    setActionTypes(state, args) {
      state.actionTypes = args;
    },
    setTriggerTypes(state, args) {
      state.triggerTypes = args;
    },
    setNotificationSeen(state, value) {
      state.metadata.updateSeen = value;
    },
    setIsSaving(state, savingState) {
      state.metadata.isSaving = savingState;
    },
    setHasUnsavedData(state, setValue) {
      state.metadata.hasUnsavedData = setValue;
    },
    setPreventSaving(state, setValue) {
      state.metadata.preventSaving = setValue;
    },
    setLastRunSuccess(state, setValue) {
      state.metadata.lastRunSuccess = setValue;
    }
  },
  actions: {
    createNewAutomation: async function (context, args) {
      if (!context.state.automations || context.state.automations.length == 0) {
        await context.dispatch("getAllAutomations");
      }
      const newName = generateNewName(
        args.name,
        context.state.automations.map((x) => {
          return { de: x };
        }),
        "de"
      );
      context.commit("setHasUnsavedData", true);
      const newAutomation = {
        automationId: Guid.empty,
        name: newName,
        isActive: false,
        isTemplate: false,
        templateId: null,
        automationRuns: [],
        automationActions: [],
        automationTriggers: [
          {
            automationTriggerId: Guid.empty,
            automationId: Guid.empty,
            triggerType: "Scheduled",
            triggerParameters: "",
            triggerCounters: ""
          }
        ]
      };
      context.commit("setCurrentAutomation", newAutomation);
    },
    createNewAutomationTemplate: async function (context, args) {
      const templateObject = context.state.automationTemplates.find((x) => x.automationId == args);
      if (templateObject) {
        const newObject = _.cloneDeep(templateObject);
        const newName = generateNewName(
          `${templateObject.name} - ${t("copy")}`,
          context.state.automations.map((x) => {
            return { de: x };
          }),
          "de"
        );
        newObject.name = newName;
        newObject.automationId = Guid.empty;
        newObject.templateId = templateObject.automationId;
        newObject.isTemplate = false;
        newObject.automationRuns = [];
        newObject.automationTriggers.map((x) => {
          x.automationId = Guid.empty;
          x.automationTriggerId = Guid.empty;
        });
        newObject.automationActions.map((x) => {
          x.automationId = Guid.empty;
          x.automationActionId = Guid.empty;
        });
        context.commit("setCurrentAutomation", newObject);
      }
      context.commit("setHasUnsavedData", true);
    },
    saveAutomation: async function (context, args) {
      context.commit("setIsSaving", true);
      context.commit("setNotificationSeen", false);
      const newAutomation = _.cloneDeep(args);

      if (!_.some(args.automationActions, { isActive: true })) {
        newAutomation.isActive = false;
      }

      for (let i = 0; i < newAutomation.automationActions.length; i++) {
        newAutomation.automationActions[i].actionParameters = JSON.stringify(args.automationActions[i].actionParameters);
      }

      const logContext = eventLogger.beginContext("saveAutomation");
      logContext.info("Initiating automation save", { ...newAutomation }).alsoToConsole();
      try {
        const result = await automationApi.createOrUpdateAutomation(newAutomation);

        context.commit("setCurrentAutomation", result.value);
        context.commit("addToAutomations", result.value);
        logContext.info("Save completed successfully", { ...args }).alsoToConsole();
        context.commit("setHasUnsavedData", false);
        context.commit("setLastRunSuccess", true);
      } catch (error) {
        context.commit("setHasUnsavedData", true);
        context.commit("setLastRunSuccess", false);
        logContext.error("Automation save completed with errors", { ...args, exception: error }).alsoToConsole();
      } finally {
        logContext.complete();
      }

      context.commit("setIsSaving", false);
    },
    getAutomation: async function (context, args) {
      const logContext = eventLogger.beginContext("loadAutomation");
      logContext.info("Initiating automation load", { ...args }).alsoToConsole();
      try {
        await context.dispatch("getAllAutomations", true);
        const automation = context.state.automations.find((x) => x.automationId.toLowerCase() == args.toLowerCase()); //await automationApi.getAutomation(args);
        if (automation) {
          logContext.info("Automation loading completed successfully", { ...args }).alsoToConsole();
          context.commit("setCurrentAutomation", automation);
        }
      } catch (error) {
        logContext.error("Automation loading completed with errors", { ...args, exception: error }).alsoToConsole();
      } finally {
        logContext.complete();
      }
    },
    getAllAutomations: async function (context, force) {
      const logContext = eventLogger.beginContext("loadAllAutomations");
      if (!force && context.state.automations && context.state.automations.length > 0) {
        logContext.info("Automations already loaded, skipping reload");
        return;
      }
      logContext.info("Initiating automation load").alsoToConsole();
      try {
        const values = await automationApi.getAllAutomations();
        logContext.info("Automation loading completed successfully").alsoToConsole();
        context.commit("setAllAutomations", values);
      } catch (error) {
        logContext.error("AllAutomations loading completed with errors", { exception: error }).alsoToConsole();
      } finally {
        logContext.complete();
      }
    },
    getAllAutomationTemplates: async function (context, force = false) {
      const logContext = eventLogger.beginContext("loadAutomationTemplates");
      if (!force && context.state.automationTemplates && context.state.automationTemplates.length > 0) {
        logContext.info("Automation templates already loaded, skipping reload");
        return;
      }
      logContext.info("Initiating automation templates load").alsoToConsole();
      try {
        const values = await automationApi.getAllAutomationTemplates();
        logContext.info("Automation templates loading completed successfully").alsoToConsole();
        context.commit("setAutomationTemplates", values);
      } catch (error) {
        logContext.error("Automations templates loading completed with errors", { exception: error }).alsoToConsole();
      } finally {
        logContext.complete();
      }
    },
    deleteAutomations: async function (context, args) {
      const logContext = eventLogger.beginContext("deleteAutomations");
      logContext.info("Initiating automation delete").alsoToConsole();
      try {
        await automationApi.deleteAutomation(args);
        logContext.info("Automation deleted successfully").alsoToConsole();
        context.commit("deleteAutomations", args);
      } catch (error) {
        logContext.error("Automation deletion completed with errors", { exception: error }).alsoToConsole();
      } finally {
        logContext.complete();
      }
    },
    activateOrDeactivateAutomation: async function (context, args) {
      const logContext = eventLogger.beginContext("activateDeactivateAutomation");
      logContext.info("Initiating Automation status change").alsoToConsole();
      try {
        const response = await automationApi.activateOrDeactivateAutomation([args.automationId], args.isActive);
        if (response && response.expiredAutomationIds && response.expiredAutomationIds.includes(args.automationId)) {
          logContext.info("Automation is expired and cannot be activated or deactivated.").alsoToConsole();
        } else {
          context.commit("updateStatus", args);
          logContext.info("Automation status updated successfully").alsoToConsole();
        }
      } catch (error) {
        console.error("error on status update", error);
      } finally {
        logContext.complete();
      }
    },
    getActionTypes: async function (context) {
      const logContext = eventLogger.beginContext("loadAllActionTypes");
      logContext.info("Initiating action types load").alsoToConsole();
      try {
        let actionTypes = await automationApi.getActionTypes();
        actionTypes = actionTypes.filter((x) => (x === "FastPage" && !context.rootState.metadata.config.feature.fastPageActionEnabled ? false : true));
        logContext.info("Action types loading completed successfully").alsoToConsole();
        context.commit("setActionTypes", actionTypes);
      } catch (error) {
        logContext.error("Action types loading completed with errors", { exception: error }).alsoToConsole();
      } finally {
        logContext.complete();
      }
    },
    getTriggerTypes: async function (context) {
      const logContext = eventLogger.beginContext("loadAllTriggerTypes");
      logContext.info("Initiating trigger types load").alsoToConsole();
      try {
        const triggerTypes = await automationApi.getTriggerTypes();
        logContext.info("Trigger types loading completed successfully").alsoToConsole();
        context.commit("setTriggerTypes", triggerTypes);
      } catch (error) {
        logContext.error("await types loading completed with errors", { exception: error }).alsoToConsole();
      } finally {
        logContext.complete();
      }
    }
  }
};
